import { SearchIcon } from "assets/icons";
import React, { forwardRef, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled, { css } from "styled-components";
import AutoComplete from "../AutoComplete/AutoComplete";

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  iconPosition?: "left" | "right";
  autocomplete?: {
    value: string;
    label: string;
  }[];
}

interface FormData {
  search: string;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ iconPosition = "left", autocomplete, ...args }, _) => {
    const { control, setValue } = useFormContext<FormData>();
    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <AutoComplete
        inputRef={inputRef}
        autocomplete={autocomplete}
        onSelect={(value) => {
          setValue("search", value);
        }}
      >
        {(ref) => (
          <SearchInputLayout style={args.style} iconPosition={iconPosition}>
            <button type="submit">
              <SearchIcon />
            </button>
            <Controller
              control={control}
              name="search"
              render={({ field: { onChange, value } }) => {
                return (
                  <input
                    type="text"
                    value={value}
                    ref={inputRef}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(e);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                    onKeyUp={(e) => {
                      if (ref.current?.length === 0) return;
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        e.stopPropagation();
                        ref.current?.[0].focus();
                      }
                    }}
                  />
                );
              }}
            />
          </SearchInputLayout>
        )}
      </AutoComplete>
    );
  },
);

SearchInput.displayName = "SearchInput";

export default SearchInput;

const SearchInputLayout = styled.div<{
  iconPosition: "left" | "right";
}>`
  position: relative;

  input {
    padding: 1.1rem 1rem;
    ${({ iconPosition }) =>
      iconPosition === "left" ? "padding-left: 4rem;" : "padding-right: 4rem;"}
    border: 1px solid ${({ theme }) => theme.color.stroke.light};
    border-radius: 0.5rem;
    background-color: ${({ theme }) => theme.color.background.white};

    flex: 1;
    width: 100%;

    ${({ theme }) => theme.typography.body3.regular};

    ::placeholder {
      color: ${({ theme }) => theme.color.text.placeholder};
    }
    :hover {
      background-color: ${({ theme }) => theme.color.grey[100]};
      border: 1px solid ${({ theme }) => theme.color.stroke.dark};
    }
    :focus {
      outline: none;
    }

    ${({ theme }) => css`
      ${theme.media.small} {
        ${theme.typography.caption1.regular}
        padding: 1.1rem 1rem;
      }
    `}
  }

  button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    ${({ iconPosition }) =>
      iconPosition === "left" ? "left: 1rem;" : "right: 1rem;"}

    color: ${({ theme }) => theme.color.icon.secondary};
  }
`;
